interface TopicAllocation {
  topic_name: string;
  percentage: number;
}

interface ProblemTopicBreakdownItem {
  problem_number: string | number;
  topic_allocations: TopicAllocation[];
}

interface ProblemTopicBreakdownProps {
  breakdown: ProblemTopicBreakdownItem[];
  documentType?: string;
}

export function ProblemTopicBreakdown({ breakdown, documentType }: ProblemTopicBreakdownProps) {

  if (!breakdown || breakdown.length === 0) {
    console.log('ProblemTopicBreakdown - returning null: no breakdown data');
    return null;
  }

  // Check if this is an exam-type document
  const isExamType = documentType?.toLowerCase().match(/exam|quiz|test|assessment/);
  console.log('ProblemTopicBreakdown - isExamType:', isExamType);
  
  if (!isExamType) {
    console.log('ProblemTopicBreakdown - returning null: not exam type');
    return null;
  }

  console.log('ProblemTopicBreakdown - RENDERING component');

  // Get unique topics across all problems for Y-axis
  const allTopics = new Set<string>();
  breakdown.forEach(problem => {
    problem.topic_allocations.forEach(allocation => {
      allTopics.add(allocation.topic_name);
    });
  });
  const uniqueTopics = Array.from(allTopics);

  // Responsive sizing based on number of problems
  const problemCount = breakdown.length;
  const isLargeDataset = problemCount > 20;
  const isVeryLargeDataset = problemCount > 40;
  
  // Calculate responsive dimensions
  const columnWidth = isVeryLargeDataset ? 'min-w-[35px]' : isLargeDataset ? 'min-w-[45px]' : 'min-w-[60px]';
  const barHeight = isVeryLargeDataset ? 'h-6' : isLargeDataset ? 'h-7' : 'h-8';
  const labelSize = isVeryLargeDataset ? 'text-[9px]' : isLargeDataset ? 'text-[10px]' : 'text-xs';
  const percentageSize = isVeryLargeDataset ? 'text-[8px]' : isLargeDataset ? 'text-[9px]' : 'text-xs';
  const topicLabelWidth = isVeryLargeDataset ? 'w-32' : isLargeDataset ? 'w-40' : 'w-48';

  // Get UMD brand colors based on percentage - all WCAG 2.1 AA compliant
  // Using white text on all bars for consistency and accessibility
  const getBarColor = (percentage: number) => {
    if (percentage >= 60) {
      // Maryland Red with white text (4.75:1 ratio - Pass)
      return { bg: 'bg-[#e21833]', text: 'text-white' };
    }
    if (percentage >= 40) {
      // Dark Gray with white text (9.6:1 ratio - Pass) 
      return { bg: 'bg-[#454545]', text: 'text-white' };
    }
    if (percentage >= 20) {
      // Medium Gray with white text - using darker shade for better contrast
      return { bg: 'bg-[#7f7f7f]', text: 'text-white' };
    }
    // Light Gray with black text (16.8:1 ratio - Pass)
    return { bg: 'bg-[#e6e6e6]', text: 'text-black' };
  };

  return (
    <div className="bg-white p-8 rounded-xl shadow-lg pdf-page-break-avoid" style={{ overflow: 'visible' }}>
      <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
        <span className="text-3xl mr-3">📊</span>
        Topic Coverage by Problem
      </h2>

      {/* Scrollable container for web display, print-scaling for PDF */}
      <div className="overflow-x-auto">
        <div className={`print-chart-container ${isVeryLargeDataset ? 'print-scale-very-large' : isLargeDataset ? 'print-scale-large' : 'print-scale-normal'}`}>
          <div className="space-y-3" style={{ minWidth: 'fit-content' }}>
          {/* Y-axis labels (topics) and bars */}
        {uniqueTopics.map((topic, topicIndex) => (
          <div key={topicIndex} className="flex items-center">
            {/* Topic label - responsive width, right aligned */}
            <div className={`${topicLabelWidth} pr-4 text-right flex-shrink-0`}>
              <span className={`${isLargeDataset ? 'text-xs' : 'text-sm'} font-medium text-gray-700 break-words`}>{topic}</span>
            </div>

            {/* Bars container - flex layout for multiple problems */}
            <div className="flex-1 flex items-center gap-0.5">
              {breakdown.map((problem, problemIndex) => {
                // Find allocation for this topic in this problem
                const allocation = problem.topic_allocations.find(
                  a => a.topic_name === topic
                );

                if (!allocation) {
                  return (
                    <div
                      key={problemIndex}
                      className={`flex-1 ${columnWidth}`}
                    />
                  );
                }

                return (
                  <div
                    key={problemIndex}
                    className={`flex-1 relative ${columnWidth}`}
                  >
                    {/* Bar */}
                    <div
                      className={`${getBarColor(allocation.percentage).bg} ${barHeight} rounded transition-all duration-300 hover:opacity-80 flex items-center justify-center`}
                      style={{ 
                        minWidth: allocation.percentage >= 10 ? `${allocation.percentage}%` : isVeryLargeDataset ? '24px' : '32px'
                      }}
                      title={`Problem ${problem.problem_number}: ${allocation.percentage}%`}
                    >
                      <span className={`${getBarColor(allocation.percentage).text} ${percentageSize} font-semibold`}>
                        {allocation.percentage}%
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

          {/* X-axis labels (problem numbers) */}
          <div className="flex items-center mt-4 pt-3 border-t border-gray-200">
            <div className={`${topicLabelWidth} pr-4 flex-shrink-0`} />
            <div className="flex-1 flex items-center gap-0.5">
              {breakdown.map((problem, index) => (
                <div
                  key={index}
                  className={`flex-1 text-center ${columnWidth}`}
                >
                  <span className={`${labelSize} font-semibold text-gray-600 block`}>
                    {isVeryLargeDataset ? `P${problem.problem_number}` : `Problem ${problem.problem_number}`}
                  </span>
                </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 pt-3 border-t border-gray-200">
        <p className="text-xs text-gray-600">
          <strong>Note:</strong> Each column represents a problem. Bar height shows the percentage 
          of that problem devoted to each topic. Empty spaces indicate the topic is not covered in that problem.
          {isLargeDataset && ' Scroll horizontally to view all problems. (PDF exports are automatically scaled for optimal rendering.)'}
        </p>
      </div>
    </div>
  );
}
